import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@shared/hooks/useStore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { flowResult } from 'mobx';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@shared/components/common/Input';
import { Button } from '@shared/components/common/Button';
import { Label } from '@shared/components/common/Label';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@shared/components/common/Form';
import IconEmailSuccess from '@shared/assets/images/icon-email-success.svg';
import { ADMIN_ROUTES, SCREEN_TYPE } from '@shared/configs/constants';
import { REGEX_VALIDATE_EMAIL } from '@shared/components/constants/common.constants';

const forgotPasswordSchema = yup.object({
    email: yup
        .string()
        .trim()
        .matches(REGEX_VALIDATE_EMAIL, 'メールアドレスが正しくありません。')
        .required('必須項目をご入力ください。')
});

type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordSchema>;

const ForgotPasswordPage = observer(() => {
    const { authStore } = useStore();
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);

    const form = useForm<ForgotPasswordFormData>({
        resolver: yupResolver(forgotPasswordSchema),
        mode: 'onChange',
        defaultValues: {
            email: ''
        }
    });

    const onSubmit = async (data: ForgotPasswordFormData) => {
        setIsLoading(true);
        try {
            const body = {
                email: data.email || '',
                screenType: SCREEN_TYPE.GA
            };
            const res = await flowResult(authStore.forgotPassword(body));
            if (res) {
                setIsEmailSent(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='min-h-screen flex items-center justify-center bg-[#F1F1F1]'>
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] md:p-10 p-4 w-[calc(100%-2rem)] sm:max-w-[640px]'>

                <h1 className='md:text-4xl text-3xl text-primary font-bold text-center mb-8'>パスワード再発行</h1>

                {isEmailSent ?
                    (
                        <div className='space-y-6'>
                            <div className='bg-[#EDF8F3] p-6 rounded flex flex-col items-center gap-4'>
                                <img className='w-[72px]' src={IconEmailSuccess} alt='email-icon' />
                                <div className='space-y-2 text-center font-medium'>
                                    <p>メールを送信しました。</p>
                                    <p>送信メール内のURLからパスワードを設定してください。</p>
                                </div>
                            </div>

                            <div className='text-center'>
                                <Link
                                    to={ADMIN_ROUTES.login.href}
                                    className='inline-flex items-center gap-1.5 font-medium group text-gray-700 hover:text-gray-900'
                                >
                                    <ArrowLeft
                                        size={20}
                                        strokeWidth={1.5}
                                        className='transition-transform duration-300 group-hover:-translate-x-1'
                                    />
                                    ログインに戻る
                                </Link>
                            </div>
                        </div>
                    ) :
                    (
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                                <FormField
                                    control={form.control}
                                    name='email'
                                    render={({ field }) => (
                                        <FormItem className='space-y-2'>
                                            <Label htmlFor='email' required>メールアドレス</Label>
                                            <p className='text-sm text-text-secondary mb-2'>
                                                登録メールアドレスを入力してください。
                                            </p>
                                            <FormControl>
                                                <Input
                                                    id='email'
                                                    type='email'
                                                    maxLength={255}
                                                    placeholder='admin@example.com'
                                                    className='h-14'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <Button
                                    type='submit'
                                    disabled={isLoading}
                                    className='w-full h-14 text-base'
                                >
                                    {isLoading ? '送信中...' : 'メール送信'}
                                </Button>

                                <div className='text-center'>
                                    <Link
                                        to={ADMIN_ROUTES.login.href}
                                        className='inline-flex items-center gap-1.5 font-medium group text-gray-700 hover:text-gray-900'
                                    >
                                        <ArrowLeft
                                            size={20}
                                            strokeWidth={1.5}
                                            className='transition-transform duration-300 group-hover:-translate-x-1'
                                        />
                                        ログインに戻る
                                    </Link>
                                </div>
                            </form>
                        </Form>
                    )}
            </div>
        </div>
    );
});

export default ForgotPasswordPage;
